import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, X, Clock, MessageCircle, Shield, BookOpen, Heart, AlertTriangle, Check, ChevronDown, Lock } from 'lucide-react';
type LegalModal = 'terms' | 'privacy' | 'refund' | null;
const legalContent: Record<Exclude<LegalModal, null>, {
  title: string;
  body: React.ReactNode;
}> = {
  terms: {
    title: 'Términos y Condiciones',
    body: <div className="space-y-4 text-sm text-charcoal/80 leading-relaxed" data-id="element-0">
        <p data-id="element-1">
          <strong data-id="element-2">1. Aceptación de los Términos</strong>
          <br data-id="element-3" />
          Al adquirir y descargar el manual "Dos Casas, los Niños y el Divorcio:
          El Método Casa Espejo", usted acepta estos términos y condiciones en
          su totalidad.
        </p>
        <p data-id="element-4">
          <strong data-id="element-5">2. Producto Digital</strong>
          <br data-id="element-6" />
          Este producto es un archivo PDF descargable. Una vez completada la
          compra, recibirá acceso inmediato al archivo. El contenido es para uso
          personal y no puede ser redistribuido, copiado ni revendido.
        </p>
        <p data-id="element-7">
          <strong data-id="element-8">3. Uso del Contenido</strong>
          <br data-id="element-9" />
          El material contenido en este manual tiene fines educativos e
          informativos. No constituye asesoramiento legal, médico ni psicológico
          profesional. Consulte a un profesional calificado para situaciones
          específicas.
        </p>
        <p data-id="element-10">
          <strong data-id="element-11">4. Propiedad Intelectual</strong>
          <br data-id="element-12" />
          Todo el contenido del manual, incluyendo texto, gráficos y diseño,
          está protegido por derechos de autor. Queda prohibida su reproducción
          total o parcial sin autorización expresa.
        </p>
        <p data-id="element-13">
          <strong data-id="element-14">5. Limitación de Responsabilidad</strong>
          <br data-id="element-15" />
          El autor y distribuidor no se hacen responsables de los resultados
          obtenidos con la aplicación de las técnicas descritas. Cada situación
          familiar es única y los resultados pueden variar.
        </p>
        <p data-id="element-16">
          <strong data-id="element-17">6. Modificaciones</strong>
          <br data-id="element-18" />
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Las modificaciones serán efectivas desde su publicación.
        </p>
      </div>
  },
  privacy: {
    title: 'Política de Privacidad',
    body: <div className="space-y-4 text-sm text-charcoal/80 leading-relaxed" data-id="element-19">
        <p data-id="element-20">
          <strong data-id="element-21">1. Información que Recopilamos</strong>
          <br data-id="element-22" />
          Recopilamos únicamente la información necesaria para procesar su
          compra: nombre, correo electrónico y datos de pago. No almacenamos
          información de tarjetas de crédito.
        </p>
        <p data-id="element-23">
          <strong data-id="element-24">2. Uso de la Información</strong>
          <br data-id="element-25" />
          Su información se utiliza exclusivamente para: procesar su pedido,
          enviar el producto digital y comunicaciones relacionadas con su
          compra.
        </p>
        <p data-id="element-26">
          <strong data-id="element-27">3. Protección de Datos</strong>
          <br data-id="element-28" />
          Implementamos medidas de seguridad estándar de la industria para
          proteger su información personal contra acceso no autorizado.
        </p>
        <p data-id="element-29">
          <strong data-id="element-30">4. No Compartimos su Información</strong>
          <br data-id="element-31" />
          No vendemos, intercambiamos ni transferimos su información personal a
          terceros, excepto los proveedores de pago necesarios para procesar su
          transacción.
        </p>
        <p data-id="element-32">
          <strong data-id="element-33">5. Contacto</strong>
          <br data-id="element-34" />
          Para cualquier consulta sobre privacidad, puede contactarnos a través
          de nuestro correo electrónico de soporte.
        </p>
      </div>
  },
  refund: {
    title: 'Política de Reembolso',
    body: <div className="space-y-4 text-sm text-charcoal/80 leading-relaxed" data-id="element-35">
        <p data-id="element-36">
          <strong data-id="element-37">Productos Digitales</strong>
          <br data-id="element-38" />
          Debido a la naturaleza digital e inmediata de este producto, todas las
          ventas son finales una vez que se ha descargado el archivo.
        </p>
        <p data-id="element-39">
          <strong data-id="element-40">Excepciones</strong>
          <br data-id="element-41" />
          Si experimenta problemas técnicos que le impidan acceder al archivo,
          contáctenos dentro de las 48 horas posteriores a la compra y
          resolveremos el problema o procesaremos un reembolso completo.
        </p>
        <p data-id="element-42">
          <strong data-id="element-43">Cómo Solicitar Soporte</strong>
          <br data-id="element-44" />
          Envíe un correo electrónico con su comprobante de compra y una
          descripción del problema. Responderemos en un plazo máximo de 24 horas
          hábiles.
        </p>
      </div>
  }
};
function LegalModalComponent({
  activeModal,
  onClose
}: {
  activeModal: Exclude<LegalModal, null>;
  onClose: () => void;
}) {
  const content = legalContent[activeModal];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-45">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-46">
        <div className="flex items-center justify-between p-6 border-b border-cream-dark" data-id="element-47">
          <h3 className="font-serif text-xl font-bold text-slate-800" data-id="element-48">
            {content.title}
          </h3>
          <button onClick={onClose} className="text-charcoal/50 hover:text-charcoal transition-colors p-1 rounded-lg hover:bg-cream" data-id="element-49">
            <X size={20} data-id="element-50" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-51">
          {content.body}
        </div>
      </motion.div>
    </motion.div>;
}
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-52">
    {children}
  </motion.div>;
const CHECKOUT_URL = 'https://guiadownload.com/checkouts/cn/hWNAXrdihXvUKtzDa2EyYCQM/en-us?_r=AQAB6C_y8vQVzRJu2XI-68dj7AlXYPBYddm8Y3_UIZV7wEs&auto_redirect=false&cart_link_id=8MRCvUUE&edge_redirect=true&preview_theme_id=158226022624&skip_shop_pay=true';
const CTAButton = ({
  className = '',
  scrollTo
}: {
  className?: string;
  scrollTo?: string;
}) => <motion.a href={scrollTo ? undefined : CHECKOUT_URL} target={scrollTo ? undefined : '_blank'} rel={scrollTo ? undefined : 'noopener noreferrer'} whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} onClick={e => {
  if (scrollTo) {
    e.preventDefault();
    document.getElementById(scrollTo)?.scrollIntoView({
      behavior: 'smooth'
    });
  }
}} className={`cursor-pointer w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center justify-center gap-1 no-underline ${className}`} data-id="element-53">
    <span className="text-lg sm:text-xl uppercase tracking-wide" data-id="element-54">
      Descargar Mi Manual Ahora
    </span>
    <span className="text-xs sm:text-sm font-normal opacity-90 flex items-center gap-1" data-id="element-55">
      <Lock size={14} data-id="element-56" /> Pago seguro. Acceso inmediato al PDF.
    </span>
  </motion.a>;
export function App() {
  const [activeModal, setActiveModal] = useState<LegalModal>(null);
  return <div className="min-h-screen bg-cream text-charcoal font-sans selection:bg-amber-200 selection:text-amber-900" data-id="element-57">
      {/* HERO SECTION */}
      <section className="bg-slate-800 text-cream pt-16 pb-20 px-4 relative overflow-hidden" data-id="element-58">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-amber-500 via-transparent to-transparent" data-id="element-59"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-60">
          <FadeIn className="text-center mb-10" data-id="element-61">
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6 text-white" data-id="element-62">
              Dos Casas, los Niños y el Divorcio: <br data-id="element-63" />
              <span className="text-amber-500 italic" data-id="element-64">
                El Método "Casa Espejo"
              </span>
            </h1>
            <p className="text-xl sm:text-2xl font-medium text-slate-300 max-w-3xl mx-auto leading-relaxed" data-id="element-65">
              Detén el llanto en la puerta de entrega y protege la salud mental
              de tus hijos sin tener que pelear con tu ex.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} className="flex flex-col items-center" data-id="element-66">
            <div className="relative w-full max-w-2xl mb-10 rounded-xl overflow-hidden shadow-2xl shadow-black/50 border border-slate-700" data-id="element-67">
              <img src="https://cdn.magicpatterns.com/uploads/smqo2KuhWVtnTQhqnLK3Ee/Tapa_final.jpg" alt="Dos Casas, los Niños y el Divorcio - Mockup del libro en múltiples dispositivos" className="w-full h-auto object-cover" data-id="element-68" />
            </div>
            <CTAButton scrollTo="precio" data-id="element-69" />
          </FadeIn>
        </div>
      </section>

      {/* THE HOOK */}
      <section className="py-20 px-4 bg-cream" data-id="element-70">
        <div className="max-w-3xl mx-auto" data-id="element-71">
          <FadeIn data-id="element-72">
            <div className="flex items-center gap-4 mb-6 text-amber-700" data-id="element-73">
              <Clock size={32} data-id="element-74" />
              <h2 className="font-serif text-3xl font-bold" data-id="element-75">
                Son las 5:55 PM de un domingo...
              </h2>
            </div>
            <div className="space-y-6 text-lg leading-relaxed text-charcoal/80" data-id="element-76">
              <p data-id="element-77">
                Estás estacionado frente a la casa de tu ex y sientes ese nudo
                en el estómago que ya conoces. Miras por el retrovisor y ves a
                tu hijo apretando su mochila, en silencio o al borde del llanto.
              </p>
              <p data-id="element-78">
                Llega el momento de la entrega y estalla el caos:{' '}
                <strong data-id="element-79">
                  "¡No me quiero ir!", "¡Me quiero quedar contigo!"
                </strong>
                . Sientes una culpa que te desgarra y te preguntas si has
                arruinado su vida para siempre.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-20 px-4 bg-white border-y border-cream-dark" data-id="element-80">
        <div className="max-w-3xl mx-auto" data-id="element-81">
          <FadeIn data-id="element-82">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8 text-slate-800" data-id="element-83">
              El trauma para un niño no es el divorcio en sí...
            </h2>
            <p className="text-xl font-medium text-amber-700 mb-8" data-id="element-84">
              Es el caos de vivir entre dos mundos que no se comunican.
            </p>
            <div className="space-y-6 text-lg leading-relaxed text-charcoal/80" data-id="element-85">
              <p data-id="element-86">
                Cuando las reglas en la casa de papá son el "recreo" y en la de
                mamá son la "disciplina", el cerebro del niño entra en estado de
                alerta constante.
              </p>
              <p data-id="element-87">
                No sabe quién debe ser hoy. Teme las transiciones porque dejar
                una casa se siente como perder a un padre cada semana. Esa
                angustia de los domingos no es rebeldía, es un grito de auxilio
                por la falta de consistencia.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} className="mt-16 bg-cream-dark/50 p-8 rounded-2xl" data-id="element-88">
            <h3 className="font-serif text-2xl font-bold mb-6 flex items-center gap-3 text-slate-800" data-id="element-89">
              <AlertTriangle className="text-amber-600" data-id="element-90" />
              Por qué lo que intentaste no funcionó:
            </h3>
            <ul className="space-y-4" data-id="element-91">
              {['Intentar imponer tus reglas en la otra casa mediante discusiones solo genera más muros defensivos en tu ex.', 'Esperar que el niño "se acostumbre solo" con el tiempo solo prolonga el estrés postraumático de la separación.', 'Ceder en todos los límites por culpa ("pobrecito, sus papás se separaron") termina creando un problema de conducta mucho más difícil de corregir.', 'Usar al niño para enviar mensajes o preguntar qué pasó en la otra casa lo pone en el papel de "árbitro", una carga emocional que ningún menor puede sostener.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-charcoal/80" data-id="element-92">
                  <XCircle className="text-red-500 shrink-0 mt-1" size={20} data-id="element-93" />
                  <span data-id="element-94">{item}</span>
                </li>)}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* THE MECHANISM */}
      <section className="py-24 px-4 bg-slate-800 text-cream" data-id="element-95">
        <div className="max-w-3xl mx-auto text-center" data-id="element-96">
          <FadeIn data-id="element-97">
            <Shield className="w-16 h-16 text-amber-500 mx-auto mb-6" data-id="element-98" />
            <h2 className="font-serif text-4xl sm:text-5xl font-bold mb-8 text-white" data-id="element-99">
              El Método "Casa Espejo"
            </h2>
            <div className="space-y-6 text-lg sm:text-xl leading-relaxed text-slate-300" data-id="element-100">
              <p data-id="element-101">
                Este manual no se trata de que tú y tu ex sean mejores amigos.
                Se trata de crear una <strong data-id="element-102">"ilusión de continuidad"</strong>.
              </p>
              <p data-id="element-103">
                Al unificar reglas invisibles (sueño, pantallas y lenguaje),
                eliminamos la ansiedad de la transición. El niño deja de sentir
                que cambia de planeta y empieza a sentir que simplemente se muda
                a su otra habitación.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 px-4 bg-cream" data-id="element-104">
        <div className="max-w-4xl mx-auto" data-id="element-105">
          <FadeIn data-id="element-106">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-center mb-16 text-slate-800" data-id="element-107">
              ¿Qué encontrarás dentro del manual?
            </h2>
            <div className="grid sm:grid-cols-2 gap-8" data-id="element-108">
              {[{
              icon: <MessageCircle data-id="element-109" />,
              title: 'El Guion de la Responsabilidad Adulta',
              desc: 'Qué decir exactamente cuando preguntan "¿Por qué te fuiste?".'
            }, {
              icon: <Clock data-id="element-110" />,
              title: 'La Regla de los 15 Minutos',
              desc: 'La técnica para eliminar el 90% de las peleas y llantos en el momento de la entrega.'
            }, {
              icon: <Shield data-id="element-111" />,
              title: 'El Método BIFF para mensajes',
              desc: 'Cómo comunicarte con tu ex de forma profesional y sin drama.'
            }, {
              icon: <CheckCircle2 data-id="element-112" />,
              title: 'Checklist de la Mochila Sin Estrés',
              desc: 'Para que nada se olvide y no haya excusas para llamadas tensas.'
            }, {
              icon: <BookOpen data-id="element-113" />,
              title: 'Micro-Guiones por Edad',
              desc: 'Respuestas listas para niños de 5 años hasta adolescentes de 18 años.'
            }].map((feature, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-cream-dark flex gap-4" data-id="element-114">
                  <div className="text-amber-600 shrink-0 bg-amber-50 p-3 rounded-lg h-fit" data-id="element-115">
                    {feature.icon}
                  </div>
                  <div data-id="element-116">
                    <h3 className="font-bold text-lg mb-2 text-slate-800" data-id="element-117">
                      {feature.title}
                    </h3>
                    <p className="text-charcoal/70" data-id="element-118">{feature.desc}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHO THIS IS FOR / NOT FOR */}
      <section className="py-20 px-4 bg-white border-y border-cream-dark" data-id="element-119">
        <div className="max-w-4xl mx-auto" data-id="element-120">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-121">
            <FadeIn data-id="element-122">
              <h3 className="font-serif text-2xl font-bold mb-6 flex items-center gap-2 text-slate-800" data-id="element-123">
                <CheckCircle2 className="text-sage" data-id="element-124" /> Para quién es esto:
              </h3>
              <ul className="space-y-4" data-id="element-125">
                {['Padres y madres recientemente separados que quieren priorizar la paz de sus hijos.', 'Familias latinas en USA que enfrentan el choque adicional de idioma y cultura entre casas.', 'Padres que sienten una culpa paralizante cada vez que dejan a sus hijos en la otra casa.', 'Hombres y mujeres que buscan una coparentalidad funcional aunque la relación con el ex sea difícil.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-charcoal/80" data-id="element-126">
                    <Check className="text-sage shrink-0 mt-1" size={20} data-id="element-127" />
                    <span data-id="element-128">{item}</span>
                  </li>)}
              </ul>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-129">
              <h3 className="font-serif text-2xl font-bold mb-6 flex items-center gap-2 text-slate-800" data-id="element-130">
                <XCircle className="text-red-400" data-id="element-131" /> Para quién NO es:
              </h3>
              <ul className="space-y-4" data-id="element-132">
                {['Casos de abuso físico o situaciones donde la seguridad del niño esté en riesgo real.', 'Personas que buscan herramientas para "ganarle" legalmente a su ex (esto es para salud emocional).', 'Padres que no están dispuestos a hacer ajustes mínimos en sus propias rutinas por el bien del niño.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-charcoal/80" data-id="element-133">
                    <XCircle className="text-red-400 shrink-0 mt-1" size={20} data-id="element-134" />
                    <span data-id="element-135">{item}</span>
                  </li>)}
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-4 bg-cream" data-id="element-136">
        <div className="max-w-3xl mx-auto" data-id="element-137">
          <FadeIn data-id="element-138">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-center mb-16 text-slate-800" data-id="element-139">
              ¿Cómo funciona?
            </h2>
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-amber-200 before:to-transparent" data-id="element-140">
              {[{
              step: '1',
              title: 'Descarga y Lee',
              desc: 'Descarga el manual y lee el Capítulo 1 sobre el "Síndrome de la Puerta" hoy mismo.'
            }, {
              step: '2',
              title: 'Aplica los Guiones',
              desc: 'Aplica los guiones de crisis en la próxima conversación o entrega con tus hijos.'
            }, {
              step: '3',
              title: 'Alinea las Reglas',
              desc: 'Usa las plantillas de negociación invisible para alinear las reglas básicas con tu ex sin generar conflicto.'
            }].map((item, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-141">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-cream bg-amber-100 text-amber-700 font-bold text-xl shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm z-10" data-id="element-142">
                    {item.step}
                  </div>
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-6 rounded-xl shadow-sm border border-cream-dark" data-id="element-143">
                    <h3 className="font-bold text-lg mb-2 text-slate-800" data-id="element-144">
                      {item.title}
                    </h3>
                    <p className="text-charcoal/70" data-id="element-145">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PROOF */}
      <section className="py-24 px-4 bg-slate-800 text-cream" data-id="element-146">
        <div className="max-w-3xl mx-auto text-center" data-id="element-147">
          <FadeIn data-id="element-148">
            <Heart className="w-12 h-12 text-amber-500 mx-auto mb-6" data-id="element-149" />
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8 text-white" data-id="element-150">
              ¿Qué cambia en 7 días?
            </h2>
            <div className="bg-slate-700/50 p-8 rounded-2xl border border-slate-600 text-left space-y-6 text-lg text-slate-300" data-id="element-151">
              <p data-id="element-152">
                En la primera semana, notarás que el nivel de cortisol (estrés)
                en tu hijo baja. Las transiciones de los domingos dejarán de ser
                una zona de guerra y empezarás a recibir respuestas más
                tranquilas en lugar de portazos o llanto.
              </p>
              <p className="font-medium text-amber-400" data-id="element-153">
                El niño recupera su derecho a ser niño, dejando de ser el
                mensajero de los adultos.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-cream" data-id="element-154">
        <div className="max-w-3xl mx-auto" data-id="element-155">
          <FadeIn data-id="element-156">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-center mb-12 text-slate-800" data-id="element-157">
              Preguntas Frecuentes
            </h2>
            <div className="space-y-6" data-id="element-158">
              {[{
              q: '¿Tengo que hablar mucho con mi ex para que esto funcione?',
              a: 'No. El método está diseñado para funcionar incluso con comunicación mínima a través de texto o correos tipo "negocios".'
            }, {
              q: '¿Funciona con adolescentes rebeldes?',
              a: 'Sí, el manual incluye una sección específica para jóvenes de 12 a 18 años y cómo manejar su resistencia a la jerarquía de dos casas.'
            }, {
              q: '¿Mi hijo dejará de llorar de inmediato?',
              a: 'El llanto disminuirá drásticamente una vez que apliques el ritual de salida y la regla de los 15 minutos, dándole la seguridad que ahora le falta.'
            }, {
              q: '¿Qué pasa si mi ex se niega a cooperar en nada?',
              a: 'El manual te enseña a blindar "tu casa" para que sea el puerto seguro que regule el sistema nervioso del niño, independientemente de lo que pase allá.'
            }].map((faq, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-cream-dark" data-id="element-159">
                  <h3 className="font-bold text-lg mb-3 text-slate-800 flex gap-3" data-id="element-160">
                    <span className="text-amber-600" data-id="element-161">Q.</span> {faq.q}
                  </h3>
                  <p className="text-charcoal/80 pl-7" data-id="element-162">{faq.a}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OFFER & CTA */}
      <section id="precio" className="py-24 px-4 bg-white border-t border-cream-dark" data-id="element-163">
        <div className="max-w-3xl mx-auto text-center" data-id="element-164">
          <FadeIn data-id="element-165">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-6 text-slate-800" data-id="element-166">
              Protege el corazón de tus hijos hoy por menos de lo que cuesta un
              café.
            </h2>
            <p className="text-xl text-charcoal/70 mb-12" data-id="element-167">
              Accede a la guía completa "Dos Casas, los Niños y el Divorcio" y
              detén el ciclo de angustia familiar ahora mismo.
            </p>

            <div className="bg-cream-dark/30 p-8 sm:p-12 rounded-3xl border-2 border-amber-200 shadow-xl max-w-xl mx-auto" data-id="element-168">
              <p className="text-sm font-bold text-amber-700 uppercase tracking-widest mb-2" data-id="element-169">
                Precio Especial de Lanzamiento
              </p>
              <div className="text-6xl sm:text-7xl font-bold text-slate-800 mb-8 font-serif" data-id="element-170">
                $7.35
              </div>
              <CTAButton className="w-full text-xl py-5" data-id="element-171" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-900 text-slate-400 py-10 text-center text-sm" data-id="element-172">
        <p data-id="element-173">
          © {new Date().getFullYear()} Método Casa Espejo. Todos los derechos
          reservados.
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs" data-id="element-174">
          <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors underline underline-offset-2" data-id="element-175">
            Términos y Condiciones
          </button>
          <span className="text-slate-600" data-id="element-176">|</span>
          <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors underline underline-offset-2" data-id="element-177">
            Política de Privacidad
          </button>
          <span className="text-slate-600" data-id="element-178">|</span>
          <button onClick={() => setActiveModal('refund')} className="hover:text-white transition-colors underline underline-offset-2" data-id="element-179">
            Política de Reembolso
          </button>
        </div>
        <p className="mt-4 text-xs opacity-60 max-w-2xl mx-auto px-4" data-id="element-180">
          Este material tiene fines educativos y no sustituye el consejo legal o
          la terapia psicológica profesional.
        </p>
      </footer>

      {/* LEGAL MODALS */}
      <AnimatePresence data-id="element-181">
        {activeModal && <LegalModalComponent activeModal={activeModal} onClose={() => setActiveModal(null)} data-id="element-182" />}
      </AnimatePresence>
    </div>;
}